import React, { useState } from 'react';
import { 
  Paper, 
  Box, 
  Typography, 
  Button, 
  List, 
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
import { Plus, Pencil, CheckCircle, Clock, XCircle, FileText } from 'lucide-react';
import { Task } from '../../types';
import { TaskDialog } from './TaskDialog';
import { TaskListItem } from './TaskListItem';
import { users } from '../../data/sampleData';

interface TaskListProps {
  tasks: Task[];
  stageId: string;
  onTaskUpdate?: (taskId: string, updates: Partial<Task>) => void;
  onTaskCreate?: (task: Partial<Task>) => void;
  onFormOpen?: (task: Task) => void;
}

export const TaskList: React.FC<TaskListProps> = ({ 
  tasks,
  stageId,
  onTaskUpdate,
  onTaskCreate,
  onFormOpen,
}) => {
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | undefined>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTask, setMenuTask] = useState<Task | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, task: Task) => {
    setAnchorEl(event.currentTarget);
    setMenuTask(task);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuTask(null);
  };

  const handleEditClick = (task: Task) => {
    setSelectedTask(task);
    setTaskDialogOpen(true);
    handleMenuClose();
  };

  const handleStatusChange = (taskId: string, newStatus: Task['status']) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const user = users.find(u => u.id === task.assignedTo);
    const userName = user ? user.name : 'Unknown user';

    onTaskUpdate?.(taskId, { 
      status: newStatus,
      metadata: task.metadata // Preserve any existing metadata
    });
  };

  const handleCompleteClick = (task: Task) => {
    if (onFormOpen) {
      onFormOpen(task);
    }
    handleMenuClose();
  };

  const handleMarkComplete = (task: Task) => {
    const user = users.find(u => u.id === task.assignedTo);
    const userName = user ? user.name : 'Unknown user';

    onTaskUpdate?.(task.id, { 
      status: 'completed',
      metadata: task.metadata // Preserve any existing metadata
    });
    handleMenuClose();
  };

  const handleTaskSave = (taskData: Partial<Task>) => {
    if (selectedTask) {
      onTaskUpdate?.(selectedTask.id, taskData);
    } else {
      onTaskCreate?.(taskData);
    }
    setTaskDialogOpen(false);
    setSelectedTask(undefined);
  };

  const hasSpecializedForm = (task: Task | null) => {
    if (!task) return false;
    return [
      'Initial Client Contact',
      'Basic Case Assessment',
      'Review Medical Records',
      'Schedule Expert Witness',
      'Draft Interrogatories'
    ].includes(task.title);
  };

  return (
    <>
      <Paper>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography component="h2" variant="h6">Tasks</Typography>
          <Button 
            startIcon={<Plus />} 
            size="small"
            onClick={() => setTaskDialogOpen(true)}
          >
            Add Task
          </Button>
        </Box>
        <Divider />
        <List>
          {tasks.map((task, index) => (
            <React.Fragment key={task.id}>
              <TaskListItem 
                task={task}
                onMenuOpen={handleMenuOpen}
                onStatusChange={handleStatusChange}
              />
              {index < tasks.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
        </List>
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => menuTask && handleEditClick(menuTask)}>
          <ListItemIcon>
            <Pencil size={16} />
          </ListItemIcon>
          Edit Task
        </MenuItem>
        {menuTask?.status !== 'completed' && (
          <>
            {menuTask?.status !== 'in-progress' && (
              <MenuItem onClick={() => menuTask && handleStatusChange(menuTask.id, 'in-progress')}>
                <ListItemIcon>
                  <Clock size={16} className="text-blue-500" />
                </ListItemIcon>
                Mark In Progress
              </MenuItem>
            )}
            {hasSpecializedForm(menuTask) && (
              <MenuItem onClick={() => menuTask && handleCompleteClick(menuTask)}>
                <ListItemIcon>
                  <FileText size={16} className="text-primary" />
                </ListItemIcon>
                Complete with Form
              </MenuItem>
            )}
            <MenuItem onClick={() => menuTask && handleMarkComplete(menuTask)}>
              <ListItemIcon>
                <CheckCircle size={16} className="text-green-500" />
              </ListItemIcon>
              Mark as Complete
            </MenuItem>
            {menuTask?.status !== 'blocked' && (
              <MenuItem onClick={() => menuTask && handleStatusChange(menuTask.id, 'blocked')}>
                <ListItemIcon>
                  <XCircle size={16} className="text-red-500" />
                </ListItemIcon>
                Mark as Blocked
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      <TaskDialog
        open={taskDialogOpen}
        onClose={() => {
          setTaskDialogOpen(false);
          setSelectedTask(undefined);
        }}
        onSave={handleTaskSave}
        task={selectedTask}
        stageId={stageId}
      />
    </>
  );
};